import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Fab, Stack, Button, Typography, Box, type Theme, type SxProps } from '@mui/material';
import { MIcon } from '../../../@material-extend';
import useLocales from '../../../../hooks/useLocales';
import { PATH_PAGE } from '../../../../routes/paths';
import { useHasPermission } from '../../../../store/useAdminStore';

const rootStyle: SxProps<Theme> = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '1 1 auto',
  minWidth: 64,
};

const backdropStyle: SxProps<Theme> = {
  position: 'fixed',
  inset: 0,
  zIndex: 1201,
};

const actionsStackStyle: SxProps<Theme> = {
  position: 'absolute',
  bottom: '100%',
  mb: 5,
  zIndex: 1202,
  alignItems: 'center',
  gap: 1.5,
};

const actionIconWrapperStyle = (iconColor: string): SxProps<Theme> => ({
  width: 32,
  height: 32,
  borderRadius: '50%',
  bgcolor: iconColor,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const actionIconStyle: SxProps<Theme> = { color: 'common.white', fontSize: 18 };

// animationDelay décalé par index pour faire apparaître les actions en cascade
const actionButtonStyle =
  (index: number, total: number): SxProps<Theme> =>
  (theme) => ({
    bgcolor: 'common.white',
    color: 'text.primary',
    borderRadius: 6,
    textTransform: 'none',
    fontWeight: 600,
    px: 2.5,
    py: 1,
    boxShadow: theme.shadows[4],
    whiteSpace: 'nowrap',
    opacity: 0,
    transform: 'translateY(16px) scale(0.9)',
    animation: 'fabItemIn 0.25s ease-out forwards',
    animationDelay: `${(total - 1 - index) * 0.07}s`,
    '@keyframes fabItemIn': {
      to: { opacity: 1, transform: 'translateY(0) scale(1)' },
    },
    '&:hover': {
      bgcolor: 'grey.100',
    },
  });

const fabStyle =
  (open: boolean): SxProps<Theme> =>
  (theme) => ({
    position: 'absolute',
    top: -28,
    zIndex: 1202,
    boxShadow: theme.shadows[4],
    transition: 'transform 0.2s',
    transform: open ? 'rotate(45deg)' : 'none',
  });

const fabIconStyle: SxProps<Theme> = { color: 'common.white', fontSize: 28 };

const AgentFab = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { translate } = useLocales();
  const hasPermission = useHasPermission();
  const canStartPatrol = hasPermission('patrol_start');
  const canDeclareEvent = hasPermission('event_create');

  const handleStartRound = () => {
    setOpen(false);
    navigate(PATH_PAGE.clockingApp.mce.resourcePatrols);
  };

  const handleDeclareEvent = () => {
    setOpen(false);
    navigate(PATH_PAGE.clockingApp.mce.resourceEvents);
  };

  const ACTIONS = [];

  if (canStartPatrol) {
    ACTIONS.push({
      label: translate('common:fab.start_round'),
      icon: 'route',
      iconColor: 'primary.main',
      onClick: handleStartRound,
    });
  }

  if (canDeclareEvent) {
    ACTIONS.push({
      label: translate('common:fab.declare_event'),
      icon: 'campaign',
      iconColor: 'warning.main',
      onClick: handleDeclareEvent,
    });
  }

  if (!ACTIONS.length) {
    return null;
  }

  return (
    <Box sx={rootStyle}>
      {open && <Box onClick={() => setOpen(false)} sx={backdropStyle} />}

      {open && (
        <Stack sx={actionsStackStyle}>
          {ACTIONS.map((action, index) => (
            <Button
              key={action.label}
              variant="contained"
              onClick={action.onClick}
              startIcon={
                <Box sx={actionIconWrapperStyle(action.iconColor)}>
                  <MIcon icon={action.icon} sx={actionIconStyle} />
                </Box>
              }
              sx={actionButtonStyle(index, ACTIONS.length)}
            >
              <Typography variant="body2" fontWeight={600}>
                {action.label}
              </Typography>
            </Button>
          ))}
        </Stack>
      )}

      <Fab
        color={open ? 'error' : 'primary'}
        onClick={() => setOpen((prev) => !prev)}
        size="medium"
        sx={fabStyle(open)}
      >
        <MIcon icon="add" sx={fabIconStyle} />
      </Fab>
    </Box>
  );
};

export default AgentFab;
